import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatSessionSummary, getChatSessionsAPI } from './sessionsApi'
import { useLabraChatContext } from './context'

/** A past chat session shown in the history list. */
export interface ChatConversation {
  /** Session UUID passed to `/chat/history` and `/chat/stream`. */
  sessionId: string
  /** Display title (the conversation's first user message). */
  title: string
  /** Owning agent, e.g. "Funding Agent". */
  agentType: string
  /** ISO-8601 of the last activity, used for the relative timestamp. */
  updatedAt: string
}

/** Page size for the sessions list. */
const PAGE_SIZE = 20

/** How long to wait after the last keystroke before searching the server. */
const SEARCH_DEBOUNCE_MS = 300

const toConversation = (session: ChatSessionSummary): ChatConversation => ({
  sessionId: session.session_id,
  title: session.conversation_title,
  agentType: session.agent_type,
  // Prefer last-update; fall back to creation, then empty (renders no time).
  updatedAt: session.updated_at || session.created_at || '',
})

export interface UseChatConversations {
  conversations: ChatConversation[]
  /** Server-reported total across all pages (drives the "N conversations" count). */
  total: number
  isLoading: boolean
  isError: boolean
  hasMore: boolean
  /** Fetch the next page and append it. No-op while loading or fully loaded. */
  loadMore: () => void
}

/**
 * Loads the current user's past chat sessions (newest-first) for the history
 * list. Fetches the first page on mount and appends further pages via
 * {@link loadMore} (offset pagination). An optional `search` term is filtered
 * **server-side** (debounced, case-insensitive title substring) so results span
 * every page, not just the ones already loaded; `total`/`hasMore` reflect the
 * filtered set. All state writes are guarded so a response that lands after
 * unmount is ignored.
 */
export const useChatConversations = (
  partnerId: string,
  search = ''
): UseChatConversations => {
  const { userId } = useLabraChatContext()
  const trimmedSearch = search.trim()

  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  // The search term actually sent to the server, debounced behind keystrokes.
  const [debouncedSearch, setDebouncedSearch] = useState(trimmedSearch)

  // Flipped on unmount so in-flight requests don't write to dead state. A ref
  // (not state) so the latest value is visible to async callbacks.
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // Debounce keystrokes into the term we query with, so typing doesn't fire a
  // request per character.
  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedSearch(trimmedSearch),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(id)
  }, [trimmedSearch])

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!partnerId || !userId) return
      setIsLoading(true)
      setIsError(false)
      try {
        const { data } = await getChatSessionsAPI(partnerId, {
          user_id: userId,
          limit: PAGE_SIZE,
          offset,
          // Omit entirely when blank so the server lists everything.
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        })
        if (!isMounted.current) return
        const page = data.sessions.map(toConversation)
        // offset 0 is a fresh load (replace); later offsets append.
        setConversations(prev => (offset === 0 ? page : [...prev, ...page]))
        setTotal(data.total)
        setHasMore(data.has_more)
      } catch {
        if (!isMounted.current) return
        setIsError(true)
      } finally {
        if (isMounted.current) setIsLoading(false)
      }
    },
    [partnerId, userId, debouncedSearch]
  )

  // (Re)load the first page whenever the partner/user or search term changes.
  useEffect(() => {
    fetchPage(0)
  }, [fetchPage])

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return
    fetchPage(conversations.length)
  }, [isLoading, hasMore, conversations.length, fetchPage])

  return { conversations, total, isLoading, isError, hasMore, loadMore }
}
